import React, { useEffect, useState } from 'react';
import { Dialog } from '@material-ui/core';
import Button from '../Buttons/Button';
import { AppCenterAPI } from 'api';
import { useAuth0 } from '@auth0/auth0-react';
import { appCode, envCode } from '../../../../config/auth0.config';
import { useInView } from 'react-intersection-observer';
import { CloseIcon, InfoIcon, SearchIcon } from 'components/icons';
import {
  ImpersonationParams,
  ImpersonationQueryUserProperties
} from 'api/app-center/app-center.types';
import { USERDATA_PROPERTY_NAME } from 'context/User.context';
import { useToasts } from 'react-toast-notifications';
import Loading from '../Loading/Loading';
import { Scrollbars } from 'react-custom-scrollbars';
interface IAssignModal {
  cancelUpdate: any;
  open: boolean;
}

const InfoBanner = (selectedUser: ImpersonationQueryUserProperties) => (
  <div className="flex bg-info-blue-light items-center px-4 py-3 gap-3 mb-8 rounded">
    <div className="text-info-blue flex justify-center items-center">
      <InfoIcon className="h-4 w-4" fill="currentColor" />
    </div>
    <p>
      Proceeding as{' '}
      <span className="font-semibold">
        {selectedUser.firstName} {selectedUser.lastName}
      </span>{' '}
      will log you out. Please log back in with the same account to proxy as the user. You can end
      the proxy session at any time by logging out.
    </p>
  </div>
);

const ProxyModal: React.FC<IAssignModal> = ({ open, cancelUpdate }) => {
  const { ref, inView } = useInView();
  const [keyword, setKeyword] = useState('');
  const [currentKeyword, setCurrentKeyword] = useState('');
  const [selectedUser, setSelectedUser] = useState<ImpersonationQueryUserProperties | null>(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const [disableProxyButtonAfterSuccess, setDisableProxyButtonAfterSuccess] = useState(false);
  const { logout, user } = useAuth0();
  const { addToast } = useToasts();

  const impersonateUser = AppCenterAPI.useImpersonateUser({
    onError: (error: any) => {
      console.error(error);
      addToast('Error occurred while impersonating user', { appearance: 'error' });
    },
    onSuccess: () => {
      setDisableProxyButtonAfterSuccess(true);
      const LIMIT = 2250;
      addToast('Proxy successful. Logging out...', {
        appearance: 'success',
        autoDismiss: true,
        autoDismissTimeout: LIMIT
      });
      setTimeout(() => {
        logout({ logoutParams: { returnTo: window.location.origin, federated: true } });
      }, LIMIT);
    }
  });

  const { data, isSuccess, hasNextPage, fetchNextPage, isLoading, isFetchingNextPage } =
    AppCenterAPI.useGetAppliedUsers({ keyword: keyword, sources: 'TeamMembers' }, !initialLoad);

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage, hasNextPage]);

  const Title = (
    <div className="pt-5 pb-[18px] px-6 flex justify-between items-center">
      <h1 className="text-lg leading-[26px] font-semibold">Proxy</h1>
      <CloseIcon
        onClick={cancelUpdate}
        className="w-[14px] h-[14px] stroke-current text-icon cursor-pointer hover:text-typography-dark"
      />
    </div>
  );

  const SearchInput = (
    <div className="px-6 w-full">
      <div className="relative w-full">
        <SearchIcon
          onClick={() => {
            setInitialLoad(false);
            setKeyword(currentKeyword);
          }}
          className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-icon cursor-pointer"
        />
        <input
          onKeyDown={e => {
            if (e.key == 'Enter') {
              setInitialLoad(false);
              setKeyword(currentKeyword);
            }
          }}
          onChange={e => setCurrentKeyword(e.target.value)}
          value={currentKeyword}
          className="w-full border border-outline-light rounded pl-8 pr-3 py-2 h-full outline-none text-sm placeholder-placeholder-text"
          type="text"
          placeholder="Search for user name or ID"
        />
      </div>
    </div>
  );

  const Row = (user: ImpersonationQueryUserProperties) => {
    return (
      <div
        className={`p-3 mx-[16px] my-[10px] border text-sm relative cursor-pointer rounded-lg shadow-[0_2px_16px_rgba(0,24,26,0.04)] ${
          `${selectedUser?.source} - ${selectedUser?.sourceId}` ===
          `${user.source} - ${user.sourceId}`
            ? 'bg-teal-background border-teal-default'
            : 'bg-white border-transparent'
        }`}
        ref={ref}
        key={`${user.source} - ${user.sourceId}`}
        onClick={() => {
          if (
            `${selectedUser?.source} - ${selectedUser?.sourceId}` ===
            `${user.source} - ${user.sourceId}`
          ) {
            setSelectedUser(null);
            return;
          }
          setSelectedUser(user);
        }}
      >
        <p className="font-semibold">{`${user.firstName} ${user.lastName}`}</p>
        <p>{user.position}</p>
        <p>{user.sourceId}</p>
      </div>
    );
  };

  const content =
    isSuccess &&
    data.pages.map(page =>
      page.map((user: ImpersonationQueryUserProperties) => {
        return Row(user);
      })
    );

  const Body = (
    <div className="w-full flex-grow px-2 pt-4 ">
      {initialLoad ? (
        <div className="w-full h-full flex justify-center items-center">
          <p className="text-lg text-typography-medium font-normal">Please search for a user</p>
        </div>
      ) : isLoading ? (
        <div className="w-full h-full flex justify-center items-center">
          <Loading delay={0} message="Loading team members" />
        </div>
      ) : (
        <Scrollbars>
          {content}
          {isFetchingNextPage && (
            <div className="h-4 w-full">
              <Loading delay={0} message="Loading more users..." />
            </div>
          )}
        </Scrollbars>
      )}
    </div>
  );

  const Footer = (
    <div className="w-full px-6 py-5 flex flex-col">
      {selectedUser && InfoBanner(selectedUser)}
      <div className="flex justify-end gap-4">
        <Button
          variant="text"
          className="text-typography-medium disabled:text-outline-dark"
          disabled={initialLoad}
          onClick={() => {
            setKeyword('');
            setCurrentKeyword('');
            setInitialLoad(true);
            setSelectedUser(null);
          }}
        >
          Clear
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!selectedUser || impersonateUser.isLoading || disableProxyButtonAfterSuccess}
          onClick={() => {
            const payload: ImpersonationParams = {
              applicationCode: appCode,
              environmentCode: envCode,
              originalUserSource:
                user?.[USERDATA_PROPERTY_NAME]?.originalUserSource ||
                user?.[USERDATA_PROPERTY_NAME]?.source ||
                '',
              originalUserSourceId:
                user?.[USERDATA_PROPERTY_NAME]?.originalUserId ||
                user?.[USERDATA_PROPERTY_NAME]?.userId ||
                0,
              effectiveFrom: null,
              effectiveTo: null,
              impersonateAsSource: selectedUser?.source || '',
              impersonateAsSourceId: selectedUser?.sourceId || 0
            };
            impersonateUser.mutate(payload);
          }}
        >
          Proxy As User
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={cancelUpdate}
      classes={{
        paper: 'rounded-lg shadow-[0_2px_16px_rgba(0,24,26,0.04)]',
        root: 'bg-typography-dark bg-opacity-[.24]'
      }}
    >
      <div className="flex h-[600px] w-[560px] flex-col">
        {Title}
        {SearchInput}
        {Body}
        {Footer}
      </div>
    </Dialog>
  );
};

export default ProxyModal;
